/**
 * A dashboard panel's failed query, as the panel shows it (ACT2-1166).
 *
 * For a workspace member the server sends a `WarehouseProblem` in the oRPC error's `data.problem`: a
 * plain headline, a next step, and the engine's own text (redacted) as its details, which the panel
 * shows under "Show details". A share-link viewer, an older server, or a failure that is not the
 * warehouse's (a malformed preset) sends only a message, which is shown as before.
 */
import type { WarehouseProblem } from '@server/warehouse-failure';

import { isAccountNeedsRepairError } from '@/_shared/components/AccountRepair/repairErrors';

export interface PanelError {
  /** The oRPC message: the problem's headline and next step, or the server's own words. */
  text: string;
  problem?: WarehouseProblem;
  /**
   * The dashboard's warehouse account was removed (ACT2-1286): the page banner owns the message and the
   * Repair, so the panel shows only a short line and no Fix. Never set from `data.problem`, which keeps
   * meaning a warehouse failure on a working account.
   */
  needsRepair?: true;
}

/** What a panel says while its dashboard needs repair; the banner above says why and offers Repair. */
export const PANEL_NEEDS_REPAIR_TEXT = "Can't run without a warehouse account";

function problemOf(error: unknown): WarehouseProblem | undefined {
  if (!error || typeof error !== 'object') return undefined;
  const data = (error as { data?: unknown }).data;
  const problem = data && typeof data === 'object' ? (data as { problem?: unknown }).problem : undefined;
  if (!problem || typeof problem !== 'object') return undefined;
  const { message, nextStep } = problem as Partial<WarehouseProblem>;
  return typeof message === 'string' && typeof nextStep === 'string' ? (problem as WarehouseProblem) : undefined;
}

export function panelError(error: unknown): PanelError | null {
  if (!error) return null;
  // Checked before `problemOf`: a needs-repair refusal is not a warehouse failure. A share-link viewer
  // never gets here with it — the server sends them a bare "Query failed".
  if (isAccountNeedsRepairError(error)) return { text: PANEL_NEEDS_REPAIR_TEXT, needsRepair: true };
  const message = error instanceof Error ? error.message : '';
  const problem = problemOf(error);
  return { text: message || 'Query failed', ...(problem ? { problem } : {}) };
}

/**
 * What the Fix button hands the dashboard assistant: the headline, and the engine's own text, which
 * names the column or table the assistant has to fix. The plain words alone do not say which.
 */
export function panelErrorForAssistant(error: PanelError): string {
  const { problem } = error;
  if (!problem) return error.text;
  return problem.details ? `${problem.message}\n\n${problem.details}` : problem.message;
}
